// ─────────────────────────────────────────────────────────────────────────────
// Phosphor icons, inline.
//
// Every path below is transcribed from `PQFORCE Mobile.dc.html`. Inline SVG on
// `currentColor` (or an explicit ramp colour) — no icon font, no sprite sheet,
// no request. The handoff's asset list is "None. All icons are inline Phosphor
// paths."
//
// These are the ONLY trusted markup constants in the app; see `trustedSvg()`.
// ─────────────────────────────────────────────────────────────────────────────

import { trustedSvg } from "home/ui/mobile/dom";
import { type StateIcon } from "home/app/mobile/types";

/**
 * A colour for an SVG attribute, and the one for the wrapping span.
 *
 * ⚠ A THEME TOKEN (`var(--color-ink-5)`) CANNOT GO INTO `fill=`/`stroke=`: presentation attributes
 * do not reliably resolve custom properties. So a token becomes `currentColor` in the markup and
 * the span's `color` — which is how the icons follow light and dark mode. A literal (the white
 * tick, the logo) goes straight into the attribute as before.
 */
function paintOf(color: string): { attr: string; span: string | null } {
    return color.startsWith("var(") ? { attr: "currentColor", span: color } : { attr: color, span: null };
}

/** `trustedSvg`, with the span coloured when the paint was a token. */
function painted(markup: string, span: string | null): HTMLSpanElement {
    const node = trustedSvg(markup);
    if (span) node.style.color = span;
    return node;
}

const PHOSPHOR = (size: number, path: string, fill = "currentColor") =>
    `<svg width="${size}" height="${size}" viewBox="0 0 256 256" fill="${fill}" aria-hidden="true">${path}</svg>`;

const PATHS = {
    house: '<path d="M219.31 108.68l-80-80a16 16 0 0 0-22.62 0l-80 80A15.87 15.87 0 0 0 32 120v96a8 8 0 0 0 8 8h64a8 8 0 0 0 8-8v-56h32v56a8 8 0 0 0 8 8h64a8 8 0 0 0 8-8v-96a15.87 15.87 0 0 0-4.69-11.32Z"></path>',
    checkSquare:
        '<path d="M216 40H40a16 16 0 0 0-16 16v144a16 16 0 0 0 16 16h176a16 16 0 0 0 16-16V56a16 16 0 0 0-16-16Zm-40.4 62.1-56 56a8 8 0 0 1-11.3 0l-24-24a8 8 0 0 1 11.3-11.3L114 141.7l50.3-50.3a8 8 0 0 1 11.3 11.3Z"></path>',
    magnifier:
        '<path d="M229.66 218.34l-50.07-50.06a88.11 88.11 0 1 0-11.31 11.31l50.06 50.07a8 8 0 0 0 11.32-11.32ZM40 112a72 72 0 1 1 72 72 72.08 72.08 0 0 1-72-72Z"></path>',
    x: '<path d="M205.66 194.34a8 8 0 0 1-11.32 11.32L128 139.31l-66.34 66.35a8 8 0 0 1-11.32-11.32L116.69 128 50.34 61.66a8 8 0 0 1 11.32-11.32L128 116.69l66.34-66.35a8 8 0 0 1 11.32 11.32L139.31 128Z"></path>',
    // Phosphor `PencilSimple` (fill): change a fact — the title, a link.
    pencil: '<path d="M227.31 73.37L182.63 28.68a16 16 0 0 0-22.63 0L36.69 152A15.86 15.86 0 0 0 32 163.31V208a16 16 0 0 0 16 16h44.69a15.86 15.86 0 0 0 11.31-4.69L227.31 96a16 16 0 0 0 0-22.63ZM192 108.68L147.31 64l24-24L216 84.68Z"></path>',
    // Phosphor `Bell` (fill): remind the assignees.
    bell: '<path d="M221.8 175.94C216.25 166.38 208 139.33 208 104a80 80 0 1 0-160 0c0 35.34-8.26 62.38-13.81 71.94A16 16 0 0 0 48 200h40.81a40 40 0 0 0 78.38 0H208a16 16 0 0 0 13.8-24.06ZM128 216a24 24 0 0 1-22.62-16h45.24A24 24 0 0 1 128 216Z"></path>',
    // The list's three segments, drawn as plain filled shapes on the 256 grid: a person (assigned to
    // me), a crown (I own), a list (all TODOs). Each button also carries its name — see renderList().
    person: '<path d="M128 28a52 52 0 1 1 0 104a52 52 0 1 1 0-104Zm0 120c-58 0-104 34-104 76v12h208v-12c0-42-46-76-104-76Z"></path>',
    crown: '<path d="M24 76l52 52 52-84 52 84 52-52-26 124H50Zm26 136h156v20H50Z"></path>',
    listAll:
        '<path d="M32 52h32v32H32Zm56 4h136v24H88ZM32 112h32v32H32Zm56 4h136v24H88ZM32 172h32v32H32Zm56 4h136v24H88Z"></path>',
    // Collapse / expand every group: two chevrons pointing in, or out.
    collapseAll: '<path d="M128 120L64 56l20-20 44 44 44-44 20 20Zm0 16l64 64-20 20-44-44-44 44-20-20Z"></path>',
    expandAll: '<path d="M128 28l64 64-20 20-44-44-44 44-20-20Zm0 200l-64-64 20-20 44 44 44-44 20 20Z"></path>',
    xCircle:
        '<path d="M128 24a104 104 0 1 0 104 104A104.11 104.11 0 0 0 128 24Zm37.66 130.34a8 8 0 0 1-11.32 11.32L128 139.31l-26.34 26.35a8 8 0 0 1-11.32-11.32L116.69 128l-26.35-26.34a8 8 0 0 1 11.32-11.32L128 116.69l26.34-26.35a8 8 0 0 1 11.32 11.32L139.31 128Z"></path>',
    calendarBlank:
        '<path d="M208 32h-24v-8a8 8 0 0 0-16 0v8H88v-8a8 8 0 0 0-16 0v8H48a16 16 0 0 0-16 16v160a16 16 0 0 0 16 16h160a16 16 0 0 0 16-16V48a16 16 0 0 0-16-16ZM72 48v8a8 8 0 0 0 16 0v-8h80v8a8 8 0 0 0 16 0v-8h24v32H48V48Zm136 160H48V96h160Z"></path>',
    flag: '<path d="M34.76 42A8 8 0 0 0 32 48v168a8 8 0 0 0 16 0v-44.23c26.79-21.16 49.87-9.75 76.45 3.41 16.4 8.11 34.06 16.85 53 16.85 13.93 0 28.54-4.75 43.82-18a8 8 0 0 0 2.76-6V48a8 8 0 0 0-13.27-6c-28 24.23-51.72 12.49-79.21-1.12C111.07 26.76 75.64 9.22 34.76 42ZM208 164.25c-26.79 21.16-49.87 9.74-76.45-3.41-25-12.38-52.33-25.9-83.55-8.4V51.79c26.79-21.16 49.87-9.75 76.45 3.4 25 12.38 52.33 25.91 83.55 8.4Z"></path>',
    trash: '<path d="M216 48h-40v-8a24 24 0 0 0-24-24h-48a24 24 0 0 0-24 24v8H40a8 8 0 0 0 0 16h8v144a16 16 0 0 0 16 16h128a16 16 0 0 0 16-16V64h8a8 8 0 0 0 0-16ZM96 40a8 8 0 0 1 8-8h48a8 8 0 0 1 8 8v8H96Z"></path>',
    plus: '<path d="M224 128a8 8 0 0 1-8 8h-80v80a8 8 0 0 1-16 0v-80H40a8 8 0 0 1 0-16h80V40a8 8 0 0 1 16 0v80h80a8 8 0 0 1 8 8Z"></path>',
    chat: '<path d="M216 48H40a16 16 0 0 0-16 16v160l40-32h152a16 16 0 0 0 16-16V64a16 16 0 0 0-16-16Z"></path>',
    warningCircle:
        '<path d="M128 24a104 104 0 1 0 104 104A104.11 104.11 0 0 0 128 24Zm-8 56a8 8 0 0 1 16 0v56a8 8 0 0 1-16 0Zm8 104a12 12 0 1 1 12-12 12 12 0 0 1-12 12Z"></path>',
    check: '<path d="M229.66 77.66l-128 128a8 8 0 0 1-11.32 0l-56-56a8 8 0 0 1 11.32-11.32L96 188.69 218.34 66.34a8 8 0 0 1 11.32 11.32Z"></path>',
    lock: '<path d="M208 80h-24V56a56 56 0 0 0-112 0v24H48a16 16 0 0 0-16 16v112a16 16 0 0 0 16 16h160a16 16 0 0 0 16-16V96a16 16 0 0 0-16-16ZM88 56a40 40 0 0 1 80 0v24H88Z"></path>',
    lockKey:
        '<path d="M208 80h-24V56a56 56 0 0 0-112 0v24H48a16 16 0 0 0-16 16v112a16 16 0 0 0 16 16h160a16 16 0 0 0 16-16V96a16 16 0 0 0-16-16ZM88 56a40 40 0 0 1 80 0v24H88Zm52 104.6V184a12 12 0 0 1-24 0v-23.4a20 20 0 1 1 24 0Z"></path>',
    envelope:
        '<path d="M230.14 58.87A16 16 0 0 0 216 48H40a16 16 0 0 0-16 16v128a16 16 0 0 0 16 16h176a16 16 0 0 0 16-16V64a16 16 0 0 0-1.86-5.13ZM128 129.09 47.16 64h161.68Z"></path>',
} as const;

export type IconName = keyof typeof PATHS;

/** A Phosphor glyph at `size`, painted with `currentColor` unless told otherwise. */
export function icon(name: IconName, size = 22, fill = "currentColor"): HTMLSpanElement {
    const paint = paintOf(fill);
    return painted(PHOSPHOR(size, PATHS[name], paint.attr), paint.span);
}

/** The back chevron — a stroked path, not a Phosphor fill. */
export function chevronLeft(color = "currentColor"): HTMLSpanElement {
    const paint = paintOf(color);
    return painted(
        `<svg width="8" height="14" viewBox="0 0 8 14" aria-hidden="true">` +
            `<path d="M7 1L1 7l6 6" stroke="${paint.attr}" stroke-width="2" fill="none" stroke-linecap="round"></path></svg>`,
        paint.span,
    );
}

/** The row chevron. */
export function chevronRight(color = "currentColor"): HTMLSpanElement {
    const paint = paintOf(color);
    return painted(
        `<svg width="7" height="12" viewBox="0 0 8 14" aria-hidden="true">` +
            `<path d="M1 1l6 6-6 6" stroke="${paint.attr}" stroke-width="2" fill="none" stroke-linecap="round"></path></svg>`,
        paint.span,
    );
}

/** The from → to arrow between two states. */
export function stateArrow(): HTMLSpanElement {
    return painted(
        `<svg width="13" height="10" viewBox="0 0 20 10" fill="none" aria-hidden="true">` +
            `<path d="M1 5h16M13 1l4 4-4 4" stroke="currentColor" stroke-width="1.6" ` +
            `stroke-linecap="round" stroke-linejoin="round"></path></svg>`,
        "var(--color-ink-6)",
    );
}

const SVG_NS = "http://www.w3.org/2000/svg";

/**
 * A lifecycle state's glyph, as the server resolved it (see `StateIcon`).
 *
 * ⚠ BUILT WITH DOM CALLS, NEVER `trustedSvg()`. The constants above are ours and may be parsed as
 * markup; this path arrived over the wire, so it goes into an attribute with `setAttribute`, which
 * cannot turn it into an element. The server already refuses anything but path commands.
 *
 * The outline is in font coordinates — y up, baseline 0, ascent 448 of 512 units — so it is
 * flipped, exactly as the configurator's IconPicker does it.
 */
export function stateGlyph(glyph: StateIcon, size: number, fill: string): SVGSVGElement {
    const width = glyph.w > 0 ? glyph.w : 512;
    const svg = document.createElementNS(SVG_NS, "svg");
    svg.setAttribute("width", String(size));
    svg.setAttribute("height", String(size));
    svg.setAttribute("viewBox", `0 -448 ${width} 512`);
    svg.setAttribute("aria-hidden", "true");
    svg.setAttribute("focusable", "false");

    const path = document.createElementNS(SVG_NS, "path");
    path.setAttribute("d", glyph.d);
    path.setAttribute("fill", fill);
    path.setAttribute("transform", "scale(1,-1)");
    svg.appendChild(path);

    return svg;
}

/** The white tick inside a completed token-setup circle. */
export function tick(size = 11, fill = "#FFFFFF"): HTMLSpanElement {
    const paint = paintOf(fill);
    return painted(PHOSPHOR(size, PATHS.check, paint.attr), paint.span);
}

// ── the PQFORCE lockup ────────────────────────────────────────────────────────

/**
 * The mark and the wordmark, transcribed from
 * `packages/js/uikit-icons/custom/icons/pqforce.svg` — the same artwork the
 * portal chrome renders as `<span uk-icon="pqforce">`, so the app and the
 * portal show one logo rather than two that nearly agree.
 *
 * ⚠ THE `<style>` BLOCK IS DELIBERATELY GONE. The source file paints its paths
 * through `.st0` / `.st1` in a `<style>` element, and a `<style>` inside INLINE
 * svg is not scoped to that svg — it is a document stylesheet. Shipping it
 * would let `.st0` repaint anything else on the page carrying that class. The
 * two fills are attributes here instead. The Illustrator preamble (`version`,
 * `id`, `x`, `y`, `enable-background`, `xml:space`) is dropped for the same
 * reason it is harmless: none of it does anything in an HTML document.
 *
 * ⚠ THE COLOURS ARE THE ARTWORK'S — #1F2146 navy and #DC5956 red — NOT the
 * app's `--color-text` / `--color-accent` (#07074E, #DB5656). Those are the
 * brand PDF's values for the same two colours and they do not match the logo
 * file. A logo is artwork, so it is reproduced as authored; retinting it to the
 * tokens is one line, but it is a decision rather than a tidy-up.
 *
 * ⚠ IT IS ARTWORK, NEVER LIVE TEXT. There is no `<text>` here and none should
 * be added: a font that fails to load would silently reset the wordmark.
 */
const LOGO_NAVY = [
    "M37.6,6.5c0.6-0.3,1.3-0.5,2.1-0.5c0.9,0,1.8,0.2,2.5,0.7c0.7,0.5,1.3,1.1,1.8,2c0.4,0.9,0.6,1.9,0.6,3 s-0.2,2.2-0.6,3c-0.4,0.9-1,1.5-1.8,2c-0.7,0.5-1.6,0.7-2.5,0.7c-0.8,0-1.5-0.2-2-0.5c-0.6-0.3-1.1-0.7-1.4-1.2v6.9h-3.4V6.2h3.4 v1.6C36.6,7.3,37.1,6.9,37.6,6.5z M40.4,9.8C40,9.3,39.4,9,38.7,9C38,9,37.4,9.3,37,9.8c-0.5,0.5-0.7,1.2-0.7,2s0.2,1.5,0.7,2 c0.5,0.5,1.1,0.7,1.7,0.7s1.3-0.3,1.7-0.8c0.5-0.5,0.7-1.2,0.7-2S40.9,10.3,40.4,9.8z",
    "M45.8,8.9c0.5-0.9,1.1-1.5,1.9-2c0.8-0.5,1.6-0.7,2.5-0.7c0.7,0,1.3,0.1,1.9,0.4c0.6,0.2,1,0.6,1.4,1V6.2h3.4 v16.5h-3.4V16c-0.4,0.5-0.9,0.8-1.4,1.1c-0.6,0.3-1.2,0.4-1.9,0.4c-0.9,0-1.7-0.2-2.5-0.7c-0.8-0.5-1.4-1.2-1.8-2 c-0.5-0.9-0.7-1.9-0.7-3C45.1,10.7,45.4,9.7,45.8,8.9z M53.2,10.3c-0.2-0.4-0.5-0.7-0.9-0.9c-0.4-0.2-0.8-0.3-1.2-0.3 c-0.4,0-0.8,0.1-1.2,0.3c-0.4,0.2-0.7,0.5-0.9,0.9c-0.2,0.4-0.4,0.9-0.4,1.5c0,0.6,0.1,1.1,0.4,1.5c0.2,0.4,0.5,0.7,0.9,0.9 c0.4,0.2,0.8,0.3,1.2,0.3c0.4,0,0.8-0.1,1.2-0.3c0.4-0.2,0.7-0.5,0.9-0.9c0.2-0.4,0.3-0.9,0.3-1.5S53.4,10.7,53.2,10.3z",
    "M64.4,9.1h-1.8v8.3h-3.4V9.1h-1.2V6.2h1.2V5.9c0-1.4,0.4-2.4,1.2-3.1c0.8-0.7,1.9-1.1,3.5-1.1 c0.3,0,0.4,0,0.6,0v2.9c-0.7,0-1.1,0.1-1.4,0.3c-0.3,0.2-0.4,0.6-0.4,1.2v0.1h1.8V9.1z",
    "M67.5,16.8c-0.9-0.5-1.6-1.1-2.1-2c-0.5-0.9-0.8-1.9-0.8-3c0-1.1,0.3-2.2,0.8-3c0.5-0.9,1.2-1.5,2.1-2 c0.9-0.5,1.9-0.7,3-0.7c1.1,0,2.1,0.2,3,0.7s1.6,1.1,2.1,2c0.5,0.9,0.8,1.9,0.8,3s-0.3,2.2-0.8,3c-0.5,0.9-1.2,1.5-2.1,2 s-1.9,0.7-3,0.7C69.4,17.5,68.4,17.3,67.5,16.8z M72.2,13.9c0.5-0.5,0.7-1.2,0.7-2.1c0-0.9-0.2-1.6-0.7-2.1 c-0.4-0.5-1-0.7-1.6-0.7c-0.7,0-1.2,0.2-1.7,0.7c-0.4,0.5-0.7,1.2-0.7,2.1c0,0.9,0.2,1.6,0.7,2.1c0.4,0.5,1,0.7,1.6,0.7 C71.1,14.6,71.7,14.3,72.2,13.9z",
    "M82.5,6.6c0.6-0.4,1.3-0.5,2-0.5v3.6h-0.9c-0.9,0-1.5,0.2-1.9,0.5c-0.4,0.4-0.6,1-0.6,1.9v5.2h-3.4V6.2H81 v1.9C81.4,7.5,81.9,7,82.5,6.6z",
    "M85.5,8.8c0.5-0.9,1.1-1.5,2-2s1.8-0.7,2.9-0.7c1.4,0,2.6,0.4,3.5,1.1c0.9,0.7,1.6,1.8,1.8,3.1h-3.6 C91.8,9.4,91.2,9,90.4,9c-0.6,0-1.1,0.2-1.5,0.7s-0.6,1.2-0.6,2.1c0,0.9,0.2,1.6,0.6,2.1c0.4,0.5,0.9,0.7,1.5,0.7 c0.9,0,1.5-0.4,1.8-1.3h3.6c-0.3,1.3-0.9,2.3-1.9,3.1c-0.9,0.7-2.1,1.1-3.5,1.1c-1.1,0-2.1-0.2-2.9-0.7s-1.5-1.1-2-2 c-0.5-0.9-0.7-1.9-0.7-3S85.1,9.6,85.5,8.8z",
    "M107.5,12.6h-7.7c0.1,0.7,0.3,1.2,0.7,1.6c0.4,0.4,0.9,0.5,1.5,0.5c0.9,0,1.4-0.4,1.8-1.1h3.6 c-0.2,0.7-0.5,1.4-1,2c-0.5,0.6-1.1,1-1.8,1.4s-1.6,0.5-2.5,0.5c-1.1,0-2.1-0.2-2.9-0.7c-0.9-0.5-1.5-1.1-2-2 c-0.5-0.9-0.7-1.9-0.7-3s0.2-2.2,0.7-3c0.5-0.9,1.1-1.5,2-2s1.8-0.7,2.9-0.7c1.1,0,2,0.2,2.9,0.7s1.5,1.1,2,1.9s0.7,1.8,0.7,2.9 C107.5,11.9,107.5,12.3,107.5,12.6z M104,10.7c0-0.6-0.2-1.1-0.6-1.4c-0.4-0.3-0.9-0.5-1.5-0.5c-0.6,0-1.1,0.2-1.5,0.5 c-0.4,0.3-0.6,0.8-0.7,1.4H104z",
];

const LOGO_RED =
    "M24.3,17.6H21c1.1-1.8,1.7-3.8,1.7-5.9c0-6.1-5-11.1-11.1-11.1S0.5,5.5,0.5,11.6v12.7c0,1.4,1.2,2.6,2.6,2.6 s2.6-1.2,2.6-2.6V21c1.8,1.1,3.8,1.7,5.9,1.7h12.8c1.4,0,2.6-1.2,2.6-2.6S25.8,17.6,24.3,17.6z M17.5,11.6c0,3.3-2.7,5.9-5.9,5.9 s-5.9-2.7-5.9-5.9s2.7-5.9,5.9-5.9S17.5,8.4,17.5,11.6z";

export function logo(height = 27): HTMLSpanElement {
    // The viewBox is 108 × 27.1, so the width follows from the height rather
    // than being a second number to keep in step.
    const width = ((height * 108) / 27.1).toFixed(1);
    return trustedSvg(
        `<svg width="${width}" height="${height}" viewBox="0 0 108 27.1" role="img" aria-label="PQFORCE">` +
            `<g class="pqm-logo-ink" fill="#1F2146">${LOGO_NAVY.map((d) => `<path d="${d}"/>`).join("")}</g>` +
            `<path fill="#DC5956" d="${LOGO_RED}"/>` +
            `</svg>`,
    );
}
